"use client"

import type React from "react"
import { useMemo, useState } from "react"
import { FaSearch, FaPlus, FaEdit, FaBan, FaCheck, FaInfoCircle, FaEye, FaSort, FaSortUp, FaSortDown, FaFileExcel, FaSpinner } from "react-icons/fa"
import {
  FiSearch,
  FiFilter,
  FiX,
  FiMapPin,
  FiCalendar,
  FiUser,
  FiRefreshCw,
  FiSettings,
  FiLayers
} from 'react-icons/fi';
import type { Vendor } from "./index"
import { Link } from "react-router-dom"
import { Pagination, PaginationType } from "../../utils/pagination.utils"
import { ExcelDownload } from "../../utils/excel.utils"
import { useDebouncedCallback } from "../../hooks/useDebounce"
import api from "../../libs/axios"
import { toast } from "react-hot-toast"
import { useAuth } from "../../provider/auth.provider"

export interface Filters {
  search?: string;
  name?: string;
  gst_number?: string;
  address?: string;
  status?: string;
  state_id?: string;
  district_id?: string;
  created_from?: string;
  created_to?: string;
  has_representatives?: string;
  project_id?: string;
  activity_id?: string;
  is_suspended?: string;
  sortBy?: string;
  sortOrder?: string;
}

type VendorWithRelations = Vendor & {
  state_circle?: {
    id?: number;
    state_name?: string | null;
    circle_name?: string | null;
  } | null;
};

interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  filters: Filters;
  onFiltersChange: (filters: Filters) => void;
  onApply: () => void;
  onReset: () => void;
}

const FilterModal: React.FC<FilterModalProps> = ({
  isOpen,
  onClose,
  filters,
  onFiltersChange,
  onApply,
  onReset
}) => {
  const [activeTab, setActiveTab] = useState('basic');

  if (!isOpen) return null;

  const handleInputChange = (field: keyof Filters, value: string) => {
    onFiltersChange({
      ...filters,
      [field]: value || undefined
    });
  };

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: FiSearch },
    // { id: 'location', label: 'Location', icon: FiMapPin },
    { id: 'dates', label: 'Dates', icon: FiCalendar },
    { id: 'associations', label: 'Associations', icon: FiLayers }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded shadow-2xl max-w-4xl w-full max-h-[90vh] min-h-[520px] flex flex-col overflow-visible">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-800 to-blue-900">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <FiFilter className="w-5 h-5" />
              Filters
            </h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <FiX className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex bg-gray-50 border-b border-gray-200 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 text-sm font-medium whitespace-nowrap flex items-center gap-2 border-b-2 transition-colors ${activeTab === tab.id
                ? 'border-blue-500 text-blue-600 bg-white'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* Basic Info Tab */}
          {activeTab === 'basic' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Name, Short Name, or GST"
                  value={filters.search || ''}
                  onChange={(e) => handleInputChange('search', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Vendor Name</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Vendor Name"
                  value={filters.name || ''}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">GST Number</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="GST Number"
                  value={filters.gst_number || ''}
                  onChange={(e) => handleInputChange('gst_number', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Address"
                  value={filters.address || ''}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.status || ''}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
            </div>
          )}

          {/* Location Tab */}
          {activeTab === 'location' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">State ID</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="State ID"
                  value={filters.state_id || ''}
                  onChange={(e) => handleInputChange('state_id', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">District ID</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="District ID"
                  value={filters.district_id || ''}
                  onChange={(e) => handleInputChange('district_id', e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Dates Tab */}
          {activeTab === 'dates' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Created From</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.created_from || ''}
                  onChange={(e) => handleInputChange('created_from', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Created To</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.created_to || ''}
                  onChange={(e) => handleInputChange('created_to', e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Associations Tab */}
          {activeTab === 'associations' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Project ID</label>
                                <input
                                    type="text"
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                    placeholder="Project ID"
                                    value={filters.project_id || ''}
                                    onChange={(e) => handleInputChange('project_id', e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Activity ID</label>
                                <input
                                    type="text"
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                    placeholder="Activity ID"
                                    value={filters.activity_id || ''}
                                    onChange={(e) => handleInputChange('activity_id', e.target.value)}
                                />
                            </div> */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Has Representatives</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.has_representatives || ''}
                  onChange={(e) => handleInputChange('has_representatives', e.target.value)}
                >
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
          <button
            onClick={onReset}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors flex items-center gap-2"
          >
            <FiRefreshCw className="w-4 h-4" />
            Reset Filters
          </button>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                onApply();
                onClose();
              }}
              className="px-6 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors flex items-center gap-2"
            >
              <FiSearch className="w-4 h-4" />
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface VendorListProps {
  vendors: Vendor[]
  pagination: PaginationType
  searchTerm: string
  statusFilter: string
  loading: boolean
  actionLoading: boolean
  onSearch: (value: string) => void
  onStatusChange: (value: string) => void
  onPageChange: (page: number) => void
  onLimitChange: (limit: number) => void
  onAddVendor: () => void
  onEditVendor: (vendor: Vendor) => void
  onToggleStatus: (vendor: Vendor) => void
  onDeleteVendor: (vendorId: number) => void
  onAdvancedFilter?: (filters: Filters) => void
  onSort?: (field: string, direction: 'ASC' | 'DESC') => void
}

export const VendorList: React.FC<VendorListProps> = ({
  vendors,
  pagination,
  searchTerm,
  statusFilter,
  loading,
  actionLoading,
  onSearch,
  onStatusChange,
  onPageChange,
  onLimitChange,
  onAddVendor,
  onEditVendor,
  onToggleStatus,
  onDeleteVendor,
  onAdvancedFilter,
  onSort
}) => {
  const { hasPermission } = useAuth()
  const totalPages = useMemo(() => pagination.total_pages || Math.ceil((pagination.total_count || 0) / (pagination.limit || 10)), [pagination])
  const [filters, setFilters] = useState<Filters>({});
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState<{
    field: string
    direction: 'ASC' | 'DESC'
  }>({
    field: 'created_at',
    direction: 'DESC'
  })
  const [isExportModalOpen, setIsExportModalOpen] = useState(false)
  const [exportData, setExportData] = useState<any[]>([])
  const [isExporting, setIsExporting] = useState(false)

  const debouncedOnSearch = useDebouncedCallback(onSearch, 400)

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFilters(prev => ({ ...prev, search: value }));
    debouncedOnSearch(value);
  }

  const handleFilterChange = (key: keyof Filters, value: string) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    if (key === 'status') {
      onStatusChange(value);
    }
    if (onAdvancedFilter) {
      onAdvancedFilter(newFilters);
    }
  };

  const handleResetFilters = () => {
    setFilters({});
    onStatusChange('all');
    onSearch('');
    if (onAdvancedFilter) {
      onAdvancedFilter({});
    }
  };

  const getActiveFiltersCount = () => {
    return Object.keys(filters).filter(key => filters[key as keyof Filters]).length;
  };

  const handleApplyFilters = () => {
    if (onAdvancedFilter) {
      onAdvancedFilter(filters);
    }
    if (filters.status !== undefined) {
      onStatusChange(filters.status);
    }
    if (filters.search !== undefined) {
      onSearch(filters.search);
    }
  };

  const handleSort = (field: string) => {
    const direction = sortConfig.field === field && sortConfig.direction === 'ASC' ? 'DESC' : 'ASC';
    setSortConfig({ field, direction });
    onSort?.(field, direction);
  };

  const getSortIcon = (field: string) => {
    if (sortConfig.field !== field) return <FaSort className="w-3 h-3" />;
    return sortConfig.direction === 'ASC' ? <FaSortUp className="w-3 h-3" /> : <FaSortDown className="w-3 h-3" />;
  };

  const handlePageClick = (page: number) => {
    if (page !== pagination.page && page >= 1 && page <= totalPages) {
      onPageChange(page)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const formatDateValue = (value?: string | null) => {
    return value ? formatDate(value) : 'N/A'
  }

  const formatPhoneNumber = (countryCode?: string | null, phone?: string | null) => {
    if (!phone) return 'N/A'
    const normalizedCode = countryCode
      ? countryCode.startsWith('+')
        ? countryCode
        : `+${countryCode}`
      : ''
    return `${normalizedCode}${phone}`.trim() || 'N/A'
  }

  const safeJoin = (values: (string | null | undefined)[]) => {
    return values.filter(value => !!value && value !== 'N/A').join('; ') || 'N/A'
  }

  const mapVendorForExport = (vendor: VendorWithRelations) => {
    const representatives = vendor.representatives || []
    const projectCount = vendor.project_associations?.length ?? 0
    const activityCount = vendor.activity_associations?.length ?? 0

    return {
      'Vendor Code': vendor.vendor_code || 'N/A',
      'Vendor Name': vendor.vendor_full_name || 'N/A',
      'Vendor Short Name': vendor.vendor_short_name || 'N/A',
      'GST Number': vendor.gst_number || 'N/A',
      Address: vendor.address || 'N/A',
      'State Name': vendor.state_circle?.state_name || 'N/A',
      'Circle Name': vendor.state_circle?.circle_name || 'N/A',
      Status: vendor.is_active ? 'Active' : 'Suspended',
      'Suspended At': formatDateValue(vendor.suspended_at),
      'Total Representatives': representatives.length,
      'Representative Names': safeJoin(representatives.map(rep => rep.contact_person)),
      'Representative Emails': safeJoin(representatives.map(rep => rep.email || undefined)),
      'Representative Phones': safeJoin(
        representatives.map(rep => formatPhoneNumber(rep.country_code, rep.contact_number))
      ),
      'Representative Locations': safeJoin(representatives.map(rep => rep.representative_location)),
      'Projects Associated': projectCount,
      'Activities Associated': activityCount,
      'Has Representatives': representatives.length > 0 ? 'Yes' : 'No',
      'Created Date': formatDateValue(vendor.created_at),
      'Last Updated': formatDateValue(vendor.updated_at),
    }
  }

  const buildExportParams = (page: number, limit: number) => {
    const params: Record<string, string | number | boolean> = {
      page,
      limit,
    }

    const effectiveSearch = (filters.search ?? searchTerm ?? '').trim()
    if (effectiveSearch) {
      params.search = effectiveSearch
    }

    const statusValue = (filters.status ?? statusFilter ?? '').toString()
    if (statusValue && statusValue !== 'all') {
      params.status = statusValue.toUpperCase()
    }

    if (filters.name?.trim()) {
      params.vendor_full_name = filters.name.trim()
    }

    if (filters.gst_number?.trim()) {
      params.gst_number = filters.gst_number.trim()
    }

    if (filters.address?.trim()) {
      params.address = filters.address.trim()
    }

    if (filters.state_id?.trim()) {
      params.state_circle_id = filters.state_id.trim()
    }

    if (filters.district_id?.trim()) {
      params.district_id = filters.district_id.trim()
    }

    if (filters.created_from) {
      params.created_at_from = filters.created_from
    }

    if (filters.created_to) {
      params.created_at_to = filters.created_to
    }

    if (filters.project_id?.trim()) {
      params.project_id = filters.project_id.trim()
    }

    if (filters.activity_id?.trim()) {
      params.activity_id = filters.activity_id.trim()
    }

    if (filters.has_representatives) {
      params.has_representatives = filters.has_representatives === 'true'
    }

    if (filters.is_suspended) {
      params.is_suspended = filters.is_suspended === 'true'
    }

    if (sortConfig.field) {
      params.sort_by = sortConfig.field
      params.sort_order = sortConfig.direction
    }

    return params
  }

  const fetchExportData = async () => {
    setIsExporting(true)
    try {
      const pageSize = 100
      let currentPage = 1
      let totalPages = 1
      const allVendors: VendorWithRelations[] = []

      do {
        const params = buildExportParams(currentPage, pageSize)
        const response = await api.get('/vendor/list', { params })

        if (!response.data.success) {
          throw new Error(response.data.message || 'Failed to fetch vendors')
        }

        const fetchedVendors: VendorWithRelations[] = response.data.data?.vendors || []
        allVendors.push(...fetchedVendors)

        const paginationInfo = response.data.data?.pagination
        const apiTotalPages = paginationInfo?.total_pages ?? paginationInfo?.totalPages ?? totalPages
        totalPages = Math.max(apiTotalPages || 1, 1)
        currentPage += 1
      } while (currentPage <= totalPages)

      if (allVendors.length === 0) {
        toast.error('No vendors found for the current filters')
        return
      }

      const mappedData = allVendors.map(mapVendorForExport)
      setExportData(mappedData)
      setIsExportModalOpen(true)
    } catch (error: any) {
      console.error('Error preparing vendor export:', error)
      toast.error(error?.response?.data?.message || 'Failed to prepare vendor export')
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <>
      <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
        {/* Header */}
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <h2 className="text-xl font-semibold text-gray-900">Vendor Management</h2>
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              {hasPermission("VendorManagement", "export") && (
                <button
                  onClick={fetchExportData}
                  disabled={isExporting}
                  className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm"
                >
                  {isExporting ? (
                    <>
                      <FaSpinner className="w-4 h-4 animate-spin" />
                      Preparing...
                    </>
                  ) : (
                    <>
                      <FaFileExcel className="w-4 h-4" />
                      Export Excel
                    </>
                  )}
                </button>
              )}
              {hasPermission("VendorManagement", "create") && (
                <button
                  onClick={onAddVendor}
                  className="bg-blue-800 hover:bg-blue-950 text-white px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm"
                >
                  <FaPlus className="w-4 h-4" />
                  Add Vendor
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50/30">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
            {/* Quick Search */}
            <div className="flex-1 max-w-md">
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Quick search by Name, Short Name, or GST..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={filters.search || ''}
                  onChange={handleSearchChange}
                />
              </div>
            </div>

            {/* Advanced Filter Button */}
            <div className="w-full sm:w-auto">
              <button
                onClick={() => setIsFilterModalOpen(true)}
                className="w-full sm:w-auto px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 flex items-center justify-center gap-2 transition-colors text-sm relative"
              >
                <FiFilter className="w-4 h-4" />
                Filters
                {getActiveFiltersCount() > 0 && (
                  <span className="absolute -top-2 -right-2 bg-blue-800 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {getActiveFiltersCount()}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Active Filters Display */}
          {getActiveFiltersCount() > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-gray-700">Active Filters:</span>
                {Object.entries(filters).map(([key, value]) =>
                  value && (
                    <span key={key} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {key.replace(/_/g, ' ')}: {value}
                      <button
                        onClick={() => {
                          const newFilters = { ...filters };
                          delete newFilters[key as keyof Filters];
                          setFilters(newFilters);
                          if (key === 'status') {
                            onStatusChange('all');
                          }
                          if (key === 'search') {
                            onSearch('');
                          }
                          if (onAdvancedFilter) {
                            onAdvancedFilter(newFilters);
                          }
                        }}
                        className="ml-1 text-blue-600 hover:text-blue-800"
                      >
                        <FiX className="w-3 h-3" />
                      </button>
                    </span>
                  )
                )}
                <button
                  onClick={handleResetFilters}
                  className="text-sm text-gray-500 hover:text-gray-700 underline"
                >
                  Clear all
                </button>
              </div>
            </div>
          )}
        </div>

        <FilterModal
          isOpen={isFilterModalOpen}
          onClose={() => setIsFilterModalOpen(false)}
          filters={filters}
          onFiltersChange={setFilters}
          onApply={handleApplyFilters}
          onReset={handleResetFilters}
        />

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">
                  Actions
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[200px] cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('vendor_full_name')}
                >
                  <div className="flex items-center gap-1">
                    Vendor Name
                    {getSortIcon('vendor_full_name')}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px] cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('vendor_short_name')}
                >
                  <div className="flex items-center gap-1">
                    Short Name
                    {getSortIcon('vendor_short_name')}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px]">
                  Vendor Code
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px]">
                  Vendor PAN
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[180px]">
                  Projects
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[140px]">
                  Activity
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px]">
                  Team Members
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px] cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('gst_number')}
                >
                  <div className="flex items-center gap-1">
                    GST Number
                    {getSortIcon('gst_number')}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[200px] cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('address')}
                >
                  <div className="flex items-center gap-1">
                    Address
                    {getSortIcon('address')}
                  </div>
                </th>
                {/* <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">Representatives</th> */}
                {/* <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px]">Associations</th> */}
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px] cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('is_active')}
                >
                  <div className="flex items-center gap-1">
                    Status
                    {getSortIcon('is_active')}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px] cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('created_at')}
                >
                  <div className="flex items-center gap-1">
                    Created At
                    {getSortIcon('created_at')}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px] cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('updated_at')}
                >
                  <div className="flex items-center gap-1">
                    Updated At
                    {getSortIcon('updated_at')}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={15} className="px-4 sm:px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
                      <span className="text-sm text-gray-600">Loading vendors...</span>
                    </div>
                  </td>
                </tr>
              ) : vendors.length === 0 ? (
                <tr>
                  <td colSpan={15} className="px-4 sm:px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                        <FaInfoCircle className="w-6 h-6 text-gray-400" />
                      </div>
                      <p className="text-sm text-gray-500">No vendors found</p>
                      <p className="text-xs text-gray-400 mt-1">Try adjusting your search or create a new vendor</p>
                    </div>
                  </td>
                </tr>
              ) : (
                vendors.map((vendor) => (
                  <tr key={vendor.id} className="hover:bg-gray-50 transition-colors">
                    {/* Actions */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        {hasPermission("VendorManagement", "update") && (
                          <button
                            onClick={() => onEditVendor(vendor)}
                            disabled={actionLoading}
                            className={`text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded transition-colors ${actionLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                            title="Edit vendor"
                          >
                            <FaEdit className="w-4 h-4" />
                          </button>
                        )}
                        <Link
                          target="_blank"
                          to={`/vendor/${vendor.id}`}
                          className={`text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded transition-colors ${actionLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                          title="View vendor"
                        >
                          <FaEye className="w-4 h-4" />
                        </Link>
                        {/* <button
                        onClick={() => onDeleteVendor(vendor.id)}
                        disabled={actionLoading}
                        className={`text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded transition-colors ${actionLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        title="Delete vendor"
                      >
                        <FaTrash className="w-4 h-4" />
                      </button> */}
                      </div>
                    </td>

                    {/* Vendor Name */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm font-semibold text-gray-900">{vendor.vendor_full_name}</span>
                    </td>

                    {/* Short Name */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {vendor.vendor_short_name}
                      </span>
                    </td>

                    {/* Vendor Code */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      {vendor.vendor_code ? (
                        <span className="text-sm font-mono text-gray-800">{vendor.vendor_code}</span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>

                    {/* Vendor PAN */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      {vendor.pan_number ? (
                        <span className="text-sm font-mono text-gray-800">{vendor.pan_number}</span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>

                    {/* Projects */}
                    <td className="px-4 py-4">
                      {(vendor.project_associations || []).length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {vendor.project_associations!.map((pa) => (
                            <span key={pa.id} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-indigo-100 text-indigo-800">
                              {pa.project?.project_name || `#${pa.project_id}`}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>

                    {/* Activity */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      {(vendor.activity_associations || []).length > 0 ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-green-100 text-green-800">
                          {vendor.activity_associations![0].activity?.activity_name || `#${vendor.activity_associations![0].activity_id}`}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>

                    {/* No of Team Members */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      {(vendor.representatives || []).length > 0 ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          {vendor.representatives!.length}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">0</span>
                      )}
                    </td>

                    {/* GST Number */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900 font-mono">{vendor.gst_number}</span>
                    </td>

                    {/* Address */}
                    <td className="px-4 py-4">
                      <span className="text-sm text-gray-600 line-clamp-2">{vendor.address}</span>
                    </td>

                    {/* Representatives */}
                    {/* <td className="px-4 py-4 whitespace-nowrap">
                    {(vendor.representatives || []).length > 0 ? (
                      <div className="space-y-1">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          {vendor.representatives.length} Rep{vendor.representatives.length !== 1 ? 's' : ''}
                        </span>
                        <div className="text-xs text-gray-500">
                          {vendor.representatives[0]?.contact_person}
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400 italic">No representatives</span>
                    )}
                  </td> */}

                    {/* Associations */}
                    {/* <td className="px-4 py-4 whitespace-nowrap">
                    <div className="space-y-1">
                      <div className="flex items-center gap-1 text-xs text-gray-600">
                        <FaInfoCircle className="w-3 h-3 text-blue-500" />
                        <span>Projects: {vendor.project_associations?.length || 0}</span>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-gray-600">
                        <FaInfoCircle className="w-3 h-3 text-green-500" />
                        <span>Activities: {vendor.activity_associations?.length || 0}</span>
                      </div>
                    </div>
                  </td> */}

                    {/* Status */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="space-y-2">
                        {vendor.is_active ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <FaCheck className="w-2 h-2" /> Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            <FaBan className="w-2 h-2" /> Suspended
                          </span>
                        )}
                        {/* <button
                        onClick={() => onToggleStatus(vendor)}
                        disabled={actionLoading}
                        className={`block text-xs text-blue-600 hover:text-blue-800 underline ${actionLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {vendor.is_active ? 'Suspend' : 'Activate'}
                      </button> */}
                      </div>
                    </td>

                    {/* Created At */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-600">{formatDate(vendor.created_at)}</span>
                    </td>

                    {/* Updated At */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-600">{formatDate(vendor.updated_at)}</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <Pagination pagination={pagination as any} onPageChange={onPageChange} onLimitChange={onLimitChange} />
      </div>

      <ExcelDownload
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        data={exportData}
        filename="vendors"
        title="Vendor Master Export"
        description="Download the vendor master with full details"
      />
    </>
  )
}
